import { useEffect, useState } from "react";
import { AiFillWarning } from "react-icons/ai";
import { RiArrowLeftLine, RiDeleteBin6Line, RiFileTransferLine, RiMoreLine } from "react-icons/ri";
import { Link, useNavigate, useParams } from "react-router-dom";

import { toast } from "@/services/toast";

import Combobox from "@/components/combobox";
import Loader from "@/components/Loader";
import Modal from "@/components/Modal";
import Toggle from "@/components/Toggle";
import Dropdown from "@/scenes/campaign/components/Dropdown";
import Information from "@/scenes/campaign/components/Information";
import Trackers from "@/scenes/campaign/components/Trackers";
import api from "@/services/api";
import { API_URL } from "@/services/config";
import { captureError } from "@/services/error";
import { withLegacyPublishers } from "@/utils/publisher";
import { isValidUrl } from "@/utils/url";

const Edit = () => {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [values, setValues] = useState({
    name: "",
    type: "",
    toPublisherId: "",
    url: "",
    trackers: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const trackedLink = `${API_URL}/r/campaign/${id}`;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/campaign/${id}`);
        if (!res.ok) {
          if (res.status === 404) {
            toast.error("Campagne non trouvée");
            navigate("/broadcast/campaigns");
            return;
          }
          throw res;
        }
        setCampaign(res.data);
        setValues({ ...values, ...res.data });
      } catch (error) {
        captureError(error, { extra: { id } });
      }
    };
    fetchData();
  }, [id]);

  const handleSubmit = async () => {
    const errors = {};

    if (!values.name) {
      errors.name = "Le nom est requis";
    }
    if (!values.type) {
      errors.type = "Le type de campagne est requis";
    }
    if (!values.toPublisherId) {
      errors.toPublisherId = "Le partenaire est requis";
    }
    if (!values.url) {
      errors.url = "L'url est requis";
    }
    if (!isValidUrl(values.url)) {
      errors.url = "L'url n'est pas valide";
    }

    if (errors.name || errors.type || errors.toPublisherId || errors.url) {
      setErrors(errors);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: values.name,
        type: values.type,
        toPublisherId: values.toPublisherId,
        url: values.url,
        urlSource: values.urlSource || "",
        trackers: values.trackers.filter((t) => t.key && t.value),
      };

      const res = await api.put(`/campaign/${id}`, payload);
      if (!res.ok) {
        if (res.status === 409) {
          return toast.error("Une campagne avec ce nom existe déjà");
        } else {
          throw res;
        }
      }
      toast.success("Campagne mise à jour");
      setCampaign(res.data);
      setValues({ ...values, ...res.data });
    } catch (error) {
      captureError(error, { extra: { id, values } });
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (activation) => {
    try {
      setValues({ ...values, active: activation });
      const res = await api.put(`/campaign/${id}`, { active: activation });
      if (!res.ok) {
        throw res;
      }
      toast.success(activation ? "Campagne activée" : "Campagne désactivée");
      setCampaign({ ...campaign, active: activation });
    } catch (error) {
      captureError(error, { extra: { id, activation } });
      setValues({ ...values, active: !activation });
    }
  };

  const handleDelete = async () => {
    const res = window.confirm("Êtes-vous sûr de vouloir supprimer cette campagne ?");
    if (!res) {
      return;
    }

    try {
      const res = await api.delete(`/campaign/${id}`);
      if (!res.ok) {
        throw res;
      }
      toast.success("Campagne supprimée");
      navigate("/broadcast/campaigns");
    } catch (error) {
      captureError(error, { extra: { id } });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(trackedLink);
    toast.success("Lien copié");
  };

  const isChanged = (v) =>
    v.name !== campaign.name || v.type !== campaign.type || v.toPublisherId !== campaign.toPublisherId || v.url !== campaign.url || v.urlSource !== campaign.urlSource;
  const isErrors = (e) => e.name || e.toPublisherId || e.url;

  if (!campaign) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <title>API Engagement - Modifier une campagne de diffusion</title>
      <ReassignModal open={isReassignModalOpen} onClose={() => setIsReassignModalOpen(false)} campaign={campaign} values={values} setValues={setValues} setCampaign={setCampaign} />
      <div className="flex flex-col gap-8">
        <Link to="/broadcast/campaigns" className="border-blue-france text-blue-france flex w-fit items-center gap-2 border-b text-[16px]">
          <RiArrowLeftLine aria-hidden="true" />
          Retour
        </Link>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-4xl font-bold">Modifier une campagne</h1>
            <p className="text-text-mention mt-2 text-base">Créée le {new Date(campaign.createdAt).toLocaleDateString("fr")}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <Toggle aria-label={values.active ? "Désactiver la campagne" : "Activer la campagne"} value={values.active} onChange={handleArchive} />
              <label className={`${values.active ? "text-blue-france" : "text-text-mention"} mb-1 ml-2`}>{values.active ? "Active" : "Inactive"}</label>
            </div>
            <button className="primary-btn" disabled={!isChanged(values) || isErrors(errors) || loading} onClick={handleSubmit}>
              {loading ? <Loader className="h-6 w-6" /> : "Enregistrer"}
            </button>
            <CampaignMenu setIsReassignModalOpen={setIsReassignModalOpen} handleDelete={handleDelete} />
          </div>
        </div>

        <div className="flex flex-col gap-8 bg-white p-4 shadow-lg sm:p-10">
          <div>
            <h2 className="mb-2 text-3xl font-bold">Paramètres</h2>
            <p className="text-text-mention text-xs">
              Les champs avec <span className="text-red-marianne">*</span> sont requis.
            </p>
          </div>

          <Information values={values} onChange={setValues} errors={errors} onErrorChange={setErrors} />
          <Trackers values={values} onChange={setValues} />

          <div className="flex flex-col">
            <p className="text-sm">Lien à insérer dans le contenu de votre campagne</p>
            <div className="border-blue-france-925 bg-blue-france-975 my-2 flex items-center justify-between border px-4 py-4">
              <span className="truncate text-sm">{trackedLink}</span>
              <button type="button" className="secondary-btn" onClick={handleCopy}>
                Copier
              </button>
            </div>
            <div>
              <span className="text-warning mt-2 flex flex-row items-center text-xs">
                <AiFillWarning className="mr-2" aria-hidden="true" />
                Copiez exactement ce lien !
              </span>
            </div>
          </div>
          <div className="col-span-2 flex justify-end gap-4">
            <button className="primary-btn" disabled={!isChanged(values) || isErrors(errors) || loading} onClick={handleSubmit}>
              {loading ? <Loader className="h-6 w-6" /> : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const CampaignMenu = ({ setIsReassignModalOpen, handleDelete }) => {
  return (
    <Dropdown
      renderTrigger={({ onClick }) => (
        <button className="tertiary-btn flex items-center" onClick={onClick} aria-label="Plus d'actions">
          <RiMoreLine className="text-xl" aria-hidden="true" />
        </button>
      )}
      position="bottom"
      align="end"
    >
      <ul className="p-1">
        <li>
          <button className="dropdown-btn w-full" onClick={() => setIsReassignModalOpen(true)}>
            <RiFileTransferLine aria-hidden="true" />
            <span>Déplacer</span>
          </button>
        </li>
        <li>
          <button className="dropdown-red-btn w-full" onClick={handleDelete}>
            <RiDeleteBin6Line aria-hidden="true" />
            <span>Supprimer</span>
          </button>
        </li>
      </ul>
    </Dropdown>
  );
};

const ReassignModal = ({ open, onClose, campaign, values, setValues, setCampaign }) => {
  const navigate = useNavigate();
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.post(`/publisher/search`, { role: "campaign" });
        if (!res.ok) {
          throw res;
        }
        setPublishers(
          withLegacyPublishers(res.data)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((p) => ({ ...p, label: p.name })),
        );
      } catch (error) {
        captureError(error);
      }
    };
    fetchData();
  }, []);

  const handleReassignSubmit = async () => {
    setLoading(true);
    try {
      if (!values.fromPublisherId) {
        toast.error("Veuillez sélectionner un partenaire");
        setLoading(false);
        return;
      }
      const res = await api.put(`/campaign/${campaign.id}/reassign`, {
        fromPublisherId: values.fromPublisherId,
      });

      if (!res.ok) {
        throw res;
      }
      toast.success("Campagne déplacée avec succès");
      setCampaign({ ...campaign, toPublisherId: values.toPublisherId });
      onClose();
      navigate("/broadcast/campaigns");
    } catch (error) {
      captureError(error, { extra: { campaign, values } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Déplacer une campagne">
      <span className="text-text-mention">
        Vers quel compte voulez-vous déplacer la campagne <span className="font-bold">{campaign.name}</span> ?
      </span>
      <div className="flex flex-1 gap-2">
        <Combobox
          id="from-publisher-id"
          options={publishers.map((p) => ({ value: p.id, label: p.name }))}
          placeholder="Sélectionner un compte"
          value={values.fromPublisherId}
          onSelect={(e) => (e ? setValues({ ...values, fromPublisherId: e.value }) : null)}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button className="tertiary-btn" type="button" onClick={onClose}>
          Annuler
        </button>
        <button
          className="primary-btn"
          type="submit"
          onClick={handleReassignSubmit}
          disabled={!values.fromPublisherId || values.fromPublisherId === campaign.fromPublisherId || loading}
        >
          {loading ? <Loader className="h-6 w-6" /> : "Valider"}
        </button>
      </div>
    </Modal>
  );
};

export default Edit;
